import React, { lazy, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useBlocker } from "react-router-dom";
import { useSelector } from "react-redux";
import { useMediaQuery } from '@genie-ui/react';
import Typography from '@genie-ui/react/v2/Typography';
import { Tabs, Tab } from '@genie-ui/react/v2';
import { Constants } from '@constants/Constants';
import { getCurrentTabFromUrl } from '@utils/utilities';
import {
  updateStateSubject,
  createStateSubscription,
  destroyStateSubscription,
} from '@gartner/genie-messenger-util';
import { ACTIS_REPORTING } from "@utils/actisReporting";
import ImportUseCasesProgressModal from "@components/molecules/ImportUseCasesProgressModal";

const HomePage = lazy(() => import('@components/pages/HomePage'));
const UseCasePage = lazy(() => import('@components/pages/UseCasePage'));
const DashboardPage = lazy(() => import('@components/pages/DashboardPage'));
const UseCaseDetailsPage = lazy(() => import('@components/pages/UseCaseDetailsPage'));
const ScorecardPage = lazy(() => import('@components/pages/ScorecardPage'));
const CaseExamplesPage = lazy(() => import('@components/pages/CaseExamplesPage'));
const CaseExamplesDetailsPage = lazy(() => import('@components/pages/CaseExamplesDetailsPage'));
const GartnerAssessmentsPage = lazy(() => import('@components/pages/GartnerAssessmentsPage'));
const ImportUseCasePage = lazy(() => import('@components/pages/ImportUseCasePage'));

import "./TabsNavigation.scss";

const {
  ROUTES: { DETAIL, SCORECARD, GARTNER_ASSESSMENTS, IMPORT_USE_CASES },
  TABS_INFO,
  TABS,
} = Constants;

const TabsNavigation = () => {
  const isMobile = useMediaQuery('(max-width:767px)');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Check if we are currently on the import screen
  const { isTaskInProgress } = useSelector((state) => state.uploadUseCases); 

  // Active tab index derived strictly from current URL pathname
  const currentTab = getCurrentTabFromUrl(pathname);

  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) => {
      const isLeavingImportScreen =
        currentLocation.pathname.includes(IMPORT_USE_CASES) &&
        !nextLocation.pathname.includes(IMPORT_USE_CASES);

      return isTaskInProgress && isLeavingImportScreen;
    },
    [isTaskInProgress]
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!isTaskInProgress && blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [isTaskInProgress, blocker]);

  // 2. Lock tab highlight value on current route when blocked
  const activeTabValue = blocker.state === 'blocked' && blocker.location
    ? getCurrentTabFromUrl(blocker.location.pathname)
    : currentTab;

  const getElementForScorecardTab = () => {
    if (pathname.includes(IMPORT_USE_CASES)) {
      return <ImportUseCasePage />;
    } else if (pathname.includes(SCORECARD)) {
      return <ScorecardPage />;
    } else if (pathname.includes(GARTNER_ASSESSMENTS)) {
      return <GartnerAssessmentsPage />;
    } else {
      return <DashboardPage />;
    }
  };

  const elements = {
    HOME_PAGE: <HomePage />,
    USE_CASES: pathname.includes(DETAIL) ? <UseCaseDetailsPage /> : <UseCasePage />,
    SCORE_CARDS: getElementForScorecardTab(),
    CASE_EXAMPLES: pathname.includes(DETAIL) ? <CaseExamplesDetailsPage /> : <CaseExamplesPage />,
  };

  const updateActisOnTabChange = (tab) => {
    ACTIS_REPORTING.TABS_NAVIGATION(TABS_INFO[tab]?.LABEL).push({
      action: Constants.ACTIS.TABS_NAVIGATION.ACTION.replace(
        Constants.ACTIS.TABS_NAVIGATION.ACTIVE_TAB, TABS_INFO[tab]?.LABEL
      ),
      label: Constants.ACTIS.TABS_NAVIGATION.LABEL,
    });
  };

  // 3. Initiate route navigation
  const navigateToPage = (event, newValue) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    const selectedValue = newValue !== undefined ? newValue : parseInt(event?.currentTarget?.value, 10);
    const route = TABS?.find(
      (item) => TABS_INFO[item].INDEX === selectedValue
    );

    if (route) {
      updateActisOnTabChange(route);
      navigate(TABS_INFO[route]?.ROUTE); // useBlocker will pause this if leaving import screen
    }
  };

  const onTabLinkClick = (tab) => {
    updateActisOnTabChange(tab);
    navigate(TABS_INFO[tab]?.ROUTE);
  };

  // 4. User clicks "Yes" / Confirm in Modal
  const handleConfirmLeave = () => {
    blocker.proceed(); // Executes trapped navigation
  };

  // 5. User clicks "No" / Cancel in Modal
  const handleCancelLeave = () => {
    blocker.reset(); // Restores state, keeps active tab on Assessments
  };

  useEffect(() => {
    const leftNavigationItemClickSubscription = createStateSubscription(
      'leftNavigationItemClick',
      (tab) => {
        if (tab) {
          onTabLinkClick(tab);
        }
      }
    );

    return () => {
      updateStateSubject('leftNavigationItemClick', '');
      destroyStateSubscription(leftNavigationItemClickSubscription);
    };
  }, []);

  const getCustomTabItem = (selectedTab) => {
    const Icon = selectedTab?.ICON;
    const isSelected = selectedTab?.INDEX === activeTabValue;

    return (
      <div className="mercury-tabsNavigation__tabItemContainer" key={selectedTab?.LABEL}>
        <Icon />
        <Typography 
          variant="para"
          fontWeight={isSelected ? "semibold" : "regular"}
          className="mercury-tabsNavigation__tabItemContainer--title"
        >
          {selectedTab?.LABEL}
        </Typography>
      </div>
    );
  };

  // Active component content driven by URL route
  const activeContent = elements[TABS[currentTab]];

  if (isMobile) {
    return (
      <>
        {activeContent}
        {blocker.state === 'blocked' && (
          <ImportUseCasesProgressModal
            showModal={true}
            handleCancelLeave={handleCancelLeave}
            handleConfirmLeave={handleConfirmLeave}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Tabs
        value={activeTabValue}
        onChange={navigateToPage}
        classes={{
          root: `mercury-tabsNavigation__tabRoot ${pathname.includes(DETAIL) ? 'mercury-tabsNavigation__tabRoot--useCaseDetail' : ''}`,
          tabContent: 'mercury-tabsNavigation__tabContent',
        }}
      >
        {TABS.map((tab) => (
          <Tab 
            key={TABS_INFO[tab].LABEL.toLowerCase()} 
            label={getCustomTabItem(TABS_INFO[tab])}
            value={TABS_INFO[tab].INDEX}
          >
             {activeContent}
          </Tab>
        ))}
      </Tabs>

      {/* Modal renders strictly when blocker is active */}
      {blocker.state === 'blocked' && (
        <ImportUseCasesProgressModal
          showModal={true}
          handleCancelLeave={handleCancelLeave}
          handleConfirmLeave={handleConfirmLeave}
        />
      )}
    </>
  );
};

export default TabsNavigation;